// Application shell: session, routing between the four screens.
//
// Routing is deliberately state in a component rather than react-router. The
// client portal has four screens and no URLs worth sharing or bookmarking —
// a questionnaire URL is meaningless without the session, and deep-linking
// into section seven of someone's tax return is not a feature anyone wants.
// A router here would be ceremony around a switch statement.
//
// The practice console is a different matter and will have real routes.

import { useCallback, useEffect, useState } from 'react';
import { ApiError, api } from './api/client.js';
import type {
  ClientProfile,
  QuestionnaireBundle,
  SessionInfo,
  UploadLimits,
  YearsResponse,
} from './api/client.js';
import { Questionnaire } from './components/Questionnaire.js';
import { SignIn } from './components/SignIn.js';
import { YearPicker } from './components/YearPicker.js';
import { YourDetails } from './components/YourDetails.js';

// The portal owns its stylesheet rather than main.tsx loading it for
// everyone. The console has its own, and a staff member should not be
// downloading the client portal’s dark theme to look at a table.
import './styles/app.css';

type Screen =
  | { name: 'loading' }
  | { name: 'signin'; linkError?: string | undefined }
  | { name: 'years' }
  | { name: 'questionnaire'; bundle: QuestionnaireBundle }
  | { name: 'details' };

export function App(): JSX.Element {
  const [screen, setScreen] = useState<Screen>({ name: 'loading' });
  const [session, setSession] = useState<SessionInfo | undefined>(undefined);
  const [years, setYears] = useState<YearsResponse | undefined>(undefined);
  const [limits, setLimits] = useState<UploadLimits | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);

  // What the client still has to supply before anything can be lodged. Kept
  // at this level because the banner shows on every screen — the client who
  // can clear the blocker should not have to go looking for it.
  const [profile, setProfile] = useState<ClientProfile | undefined>(undefined);

  // -------------------------------------------------------------------------
  // Boot
  // -------------------------------------------------------------------------
  useEffect(() => {
    // A magic link that has expired or been used redirects back with ?error.
    // Read it, then clean the URL so a refresh does not show the message
    // again and so it never ends up in a shared link.
    const params = new URLSearchParams(window.location.search);
    const linkError = params.get('error');
    if (linkError) {
      window.history.replaceState(null, '', window.location.pathname);
    }

    const errorMessage =
      linkError === 'link-expired'
        ? 'That link has expired or was already used. Enter your email and we will send a fresh one.'
        : linkError === 'link-invalid'
          ? 'That link was not valid. Enter your email and we will send a new one.'
          : undefined;

    void (async () => {
      try {
        const current = await api.session();

        // Staff who land on the client portal by habit get sent to their own
        // console rather than a confusing empty screen.
        if (current.kind === 'staff') {
          window.location.href = '/practice';
          return;
        }

        setSession(current);
        const [yearsData, limitsData] = await Promise.all([api.years(), api.uploadLimits()]);
        setYears(yearsData);
        setLimits(limitsData);
        setScreen({ name: 'years' });
      } catch (caught) {
        if (caught instanceof ApiError && caught.isUnauthenticated) {
          setScreen({ name: 'signin', linkError: errorMessage });
          return;
        }
        setError(
          caught instanceof ApiError ? caught.message : 'Could not reach MyShoebox. Please retry.',
        );
        setScreen({ name: 'signin', linkError: errorMessage });
      }
    })();
  }, []);

  // -------------------------------------------------------------------------
  // Navigation
  // -------------------------------------------------------------------------
  const openYear = useCallback(async (year: number) => {
    setScreen({ name: 'loading' });
    try {
      const bundle = await api.year(year);
      setScreen({ name: 'questionnaire', bundle });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not open that year.');
      setScreen({ name: 'years' });
    }
  }, []);

  const backToYears = useCallback(async () => {
    setScreen({ name: 'loading' });
    try {
      // Refetched rather than cached: the counts and status will have changed
      // while the client was working, and showing stale ones undermines trust
      // in everything else on the screen.
      setYears(await api.years());
    } catch {
      // Keep whatever we had. Not worth blocking navigation over.
    }
    setScreen({ name: 'years' });
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.signOut();
    } finally {
      window.location.href = '/';
    }
  }, []);

  // -------------------------------------------------------------------------
  // Outstanding details
  // -------------------------------------------------------------------------
  const loadProfile = useCallback(async () => {
    try {
      setProfile(await api.profile());
    } catch {
      // Not fatal. A client who cannot load their profile can still answer
      // questions; they just do not get the banner, and the firm sees the
      // same gap in the console regardless.
    }
  }, []);

  useEffect(() => {
    if (session?.kind === 'client') void loadProfile();
  }, [session, loadProfile]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  switch (screen.name) {
    case 'loading':
      return (
        <div className="splash">
          <div className="spinner" role="status" aria-label="Loading" />
        </div>
      );

    case 'signin':
      return <SignIn linkError={screen.linkError ?? error} />;

    case 'details':
      return (
        <YourDetails
          onDone={() => setScreen({ name: 'years' })}
          onSaved={() => void loadProfile()}
        />
      );

    case 'years':
      if (!years || !session || session.kind !== 'client') {
        return (
          <div className="splash">
            <div className="spinner" role="status" aria-label="Loading" />
          </div>
        );
      }
      return (
        <>
          <MissingDetailsBanner profile={profile} onFix={() => setScreen({ name: 'details' })} />
          <YearPicker
            data={years}
            clientName={session.user.name}
            onOpen={(year) => void openYear(year)}
            onSignOut={() => void signOut()}
            onDetails={() => setScreen({ name: 'details' })}
          />
        </>
      );

    case 'questionnaire':
      if (!limits) {
        return (
          <div className="splash">
            <div className="spinner" role="status" aria-label="Loading" />
          </div>
        );
      }
      return (
        <>
          <MissingDetailsBanner profile={profile} onFix={() => setScreen({ name: 'details' })} />
          <Questionnaire
            bundle={screen.bundle}
            limits={limits}
            onLeave={() => void backToYears()}
            onSubmitted={() => void backToYears()}
          />
        </>
      );

    default: {
      const exhaustive: never = screen;
      return <>{String(exhaustive)}</>;
    }
  }
}

// ---------------------------------------------------------------------------
// Missing details banner
// ---------------------------------------------------------------------------

/**
 * What is still needed before this return can be lodged.
 *
 * Shown on every screen, not tucked away on a settings page, because the
 * client is the only person who can clear it and they are not going looking.
 * The firm sees the same gap as a warning icon in their grid; this is the
 * other half of that, pointed at the person who can actually fix it.
 *
 * Deliberately not dismissible. It disappears when the details are there,
 * which is the only way it should disappear.
 */
function MissingDetailsBanner({
  profile,
  onFix,
}: {
  profile: ClientProfile | undefined;
  onFix: () => void;
}): JSX.Element | null {
  if (!profile || profile.missingForFiling.length === 0) return null;

  const list = profile.missingForFiling;
  const readable =
    list.length === 1 ? list[0] : `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;

  return (
    // <output> rather than a div with role="status": it is the semantic
    // element for something the page worked out, it is a polite live region
    // already, and it means a client who clears one field hears about the
    // one still outstanding.
    <output className="missing">
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        aria-hidden="true"
      >
        <path d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
      </svg>
      <span className="missing__text">
        We still need <strong>{readable}</strong> before your return can be lodged.
      </span>
      <button type="button" className="missing__fix" onClick={onFix}>
        Add them
      </button>
    </output>
  );
}
